// utils/permissions.ts
// Permission and role-based access control utilities with TypeScript types

import { ROLES, ROLE_HIERARCHY, type RoleValue } from '../config/constants';
import type { AccountType, TournamentRole } from '../types/roles.types';

// ============================================
// TYPES
// ============================================

export interface UserWithRole {
  // New system - global account type
  accountType?: AccountType;
  // Legacy system - global role (being phased out)
  role?: RoleValue;
  // Tournament-specific role (set per-tournament)
  tournamentRole?: TournamentRole;
  // Per-tournament staff assignments: { [tournamentId]: { role } } (newer)
  // or { [tournamentId]: true } (legacy). The role here is the base for
  // tournament-scoped permissions.
  tournamentAssignments?: Record<string, { role?: string } | boolean | unknown>;
  // Regular floor-team membership: { [tournamentId]: teamLetter }
  floorTeamAssignments?: Record<string, unknown>;
  // Store-manager (proxy) delegation
  permanentStoreId?: string;
  permanentStoreRole?: string;
  // Team assignments
  actingTeamLeadFor?: string | null;
  headJudgeTeamAssignments?: Record<string, unknown>;
  featureMatchTeamAssignments?: Record<string, unknown>;
  deckCheckTeamAssignments?: Record<string, unknown>;
}

// ============================================
// PERMISSIONS ENUM
// ============================================

export const PERMISSIONS = {
  // Tournament management
  CREATE_TOURNAMENT: 'create_tournament',
  EDIT_TOURNAMENT: 'edit_tournament',
  DELETE_TOURNAMENT: 'delete_tournament',
  ARCHIVE_TOURNAMENT: 'archive_tournament',

  // Table management
  VIEW_TABLES: 'view_tables',
  EDIT_TABLE_RESULT: 'edit_table_result',
  RECORD_NO_SHOW: 'record_no_show',
  FEATURE_TABLE: 'feature_table',

  // Player management
  VIEW_PLAYERS: 'view_players',
  UPLOAD_PLAYERS: 'upload_players',
  EDIT_PLAYER: 'edit_player',
  ISSUE_PENALTY: 'issue_penalty',
  DROP_PLAYER: 'drop_player',

  // Appeals
  VIEW_APPEALS: 'view_appeals',
  TAKE_APPEAL: 'take_appeal',
  CONCLUDE_APPEAL: 'conclude_appeal',
  VIEW_APPEAL_LOG: 'view_appeal_log',

  // Penalties
  APPROVE_PENALTY: 'approve_penalty',
  REJECT_PENALTY: 'reject_penalty',

  // Staff
  VIEW_STAFF: 'view_staff',
  MANAGE_STAFF: 'manage_staff',
  ASSIGN_STAFF: 'assign_staff',

  // Timer
  VIEW_TIMER: 'view_timer',
  CONTROL_TIMER: 'control_timer',

  // Admin
  APPROVE_USERS: 'approve_users',
  VIEW_ADMIN_DASHBOARD: 'view_admin_dashboard',
  MANAGE_FEATURE_REQUESTS: 'manage_feature_requests',
  MANAGE_RETENTION_POLICY: 'manage_retention_policy',
  VIEW_ANALYTICS: 'view_analytics',

  // Deck Check
  VIEW_DECK_CHECKS: 'view_deck_checks',
  CREATE_DECK_CHECK: 'create_deck_check',
  COMPLETE_DECK_CHECK: 'complete_deck_check',

  // Tournament Engine - Core
  INIT_TOURNAMENT_ENGINE: 'init_tournament_engine',
  START_TOURNAMENT: 'start_tournament',
  END_TOURNAMENT: 'end_tournament',

  // Tournament Engine - Rounds
  GENERATE_PAIRINGS: 'generate_pairings',
  COMPLETE_ROUND: 'complete_round',
  START_TOP_CUT: 'start_top_cut',

  // Tournament Engine - Results
  ENTER_RESULT: 'enter_result',
  EDIT_RESULT: 'edit_result',

  // Tournament Engine - Players
  ADD_TOURNAMENT_PLAYER: 'add_tournament_player',
  DROP_TOURNAMENT_PLAYER: 'drop_tournament_player',
  UNDROP_TOURNAMENT_PLAYER: 'undrop_tournament_player',
  EDIT_TOURNAMENT_PLAYER: 'edit_tournament_player',

  // Tournament Engine - Repairs
  REPAIR_PAIRINGS: 'repair_pairings',

  // Tournament Engine - Settings
  EDIT_TOURNAMENT_SETTINGS: 'edit_tournament_settings',

  // Tournament Engine - Viewing
  VIEW_STANDINGS: 'view_standings',
  VIEW_PAIRINGS: 'view_pairings',
  VIEW_BRACKET: 'view_bracket',
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

// ============================================
// ROLE DISPLAY NAMES
// ============================================

const roleDisplayNames: Record<RoleValue, string> = {
  [ROLES.ADMIN]: 'Administrator',
  [ROLES.MANAGER]: 'Manager',
  [ROLES.HEAD_JUDGE]: 'Head Judge',
  [ROLES.SCOREKEEPER]: 'Scorekeeper',
  [ROLES.SHIELD]: 'Shield',
  [ROLES.TEAM_LEAD]: 'Team Lead',
  [ROLES.FLOOR_JUDGE]: 'Floor Judge',
  [ROLES.FEATURE_MATCH_JUDGE]: 'Feature Match Judge',
  [ROLES.STREAM]: 'Stream Operator',
  [ROLES.PENDING]: 'Pending Approval',
};

const roleColors: Record<RoleValue, string> = {
  [ROLES.ADMIN]: '#FF3366',
  [ROLES.MANAGER]: '#FF6B35',
  [ROLES.HEAD_JUDGE]: '#FFE600',
  [ROLES.SCOREKEEPER]: '#00FFD4',
  [ROLES.SHIELD]: '#7B68EE',
  [ROLES.TEAM_LEAD]: '#00FF88',
  [ROLES.FLOOR_JUDGE]: '#8892A0',
  [ROLES.FEATURE_MATCH_JUDGE]: '#4A5568',
  [ROLES.STREAM]: '#9C27B0',
  [ROLES.PENDING]: '#FFE600',
};

// ============================================
// ROLE PERMISSIONS MAPPING
// ============================================

const rolePermissions: Record<RoleValue, Permission[]> = {
  [ROLES.ADMIN]: Object.values(PERMISSIONS),

  [ROLES.MANAGER]: [
    PERMISSIONS.CREATE_TOURNAMENT,
    PERMISSIONS.EDIT_TOURNAMENT,
    PERMISSIONS.ARCHIVE_TOURNAMENT,
    PERMISSIONS.VIEW_TABLES,
    PERMISSIONS.EDIT_TABLE_RESULT,
    PERMISSIONS.RECORD_NO_SHOW,
    PERMISSIONS.FEATURE_TABLE,
    PERMISSIONS.VIEW_PLAYERS,
    PERMISSIONS.UPLOAD_PLAYERS,
    PERMISSIONS.EDIT_PLAYER,
    PERMISSIONS.ISSUE_PENALTY,
    PERMISSIONS.DROP_PLAYER,
    PERMISSIONS.VIEW_APPEALS,
    PERMISSIONS.TAKE_APPEAL,
    PERMISSIONS.CONCLUDE_APPEAL,
    PERMISSIONS.VIEW_APPEAL_LOG,
    PERMISSIONS.APPROVE_PENALTY,
    PERMISSIONS.REJECT_PENALTY,
    PERMISSIONS.VIEW_STAFF,
    PERMISSIONS.MANAGE_STAFF,
    PERMISSIONS.ASSIGN_STAFF,
    PERMISSIONS.VIEW_TIMER,
    PERMISSIONS.CONTROL_TIMER,
    PERMISSIONS.APPROVE_USERS,
    PERMISSIONS.VIEW_DECK_CHECKS,
    PERMISSIONS.CREATE_DECK_CHECK,
    PERMISSIONS.COMPLETE_DECK_CHECK,
    PERMISSIONS.INIT_TOURNAMENT_ENGINE,
    PERMISSIONS.START_TOURNAMENT,
    PERMISSIONS.END_TOURNAMENT,
    PERMISSIONS.GENERATE_PAIRINGS,
    PERMISSIONS.COMPLETE_ROUND,
    PERMISSIONS.START_TOP_CUT,
    PERMISSIONS.ENTER_RESULT,
    PERMISSIONS.EDIT_RESULT,
    PERMISSIONS.ADD_TOURNAMENT_PLAYER,
    PERMISSIONS.DROP_TOURNAMENT_PLAYER,
    PERMISSIONS.UNDROP_TOURNAMENT_PLAYER,
    PERMISSIONS.EDIT_TOURNAMENT_PLAYER,
    PERMISSIONS.REPAIR_PAIRINGS,
    PERMISSIONS.EDIT_TOURNAMENT_SETTINGS,
    PERMISSIONS.VIEW_STANDINGS,
    PERMISSIONS.VIEW_PAIRINGS,
    PERMISSIONS.VIEW_BRACKET,
  ],

  [ROLES.HEAD_JUDGE]: [
    PERMISSIONS.VIEW_TABLES,
    PERMISSIONS.EDIT_TABLE_RESULT,
    PERMISSIONS.RECORD_NO_SHOW,
    PERMISSIONS.VIEW_PLAYERS,
    PERMISSIONS.ISSUE_PENALTY,
    PERMISSIONS.VIEW_APPEALS,
    PERMISSIONS.TAKE_APPEAL,
    PERMISSIONS.CONCLUDE_APPEAL,
    PERMISSIONS.VIEW_APPEAL_LOG,
    PERMISSIONS.APPROVE_PENALTY,
    PERMISSIONS.REJECT_PENALTY,
    PERMISSIONS.VIEW_STAFF,
    PERMISSIONS.VIEW_TIMER,
    PERMISSIONS.CONTROL_TIMER,
    PERMISSIONS.VIEW_DECK_CHECKS,
    PERMISSIONS.CREATE_DECK_CHECK,
    PERMISSIONS.COMPLETE_DECK_CHECK,
    PERMISSIONS.START_TOURNAMENT,
    PERMISSIONS.GENERATE_PAIRINGS,
    PERMISSIONS.COMPLETE_ROUND,
    PERMISSIONS.START_TOP_CUT,
    PERMISSIONS.ENTER_RESULT,
    PERMISSIONS.EDIT_RESULT,
    PERMISSIONS.ADD_TOURNAMENT_PLAYER,
    PERMISSIONS.DROP_TOURNAMENT_PLAYER,
    PERMISSIONS.UNDROP_TOURNAMENT_PLAYER,
    PERMISSIONS.EDIT_TOURNAMENT_PLAYER,
    PERMISSIONS.REPAIR_PAIRINGS,
    PERMISSIONS.VIEW_STANDINGS,
    PERMISSIONS.VIEW_PAIRINGS,
    PERMISSIONS.VIEW_BRACKET,
  ],

  [ROLES.SCOREKEEPER]: [
    PERMISSIONS.CREATE_TOURNAMENT,
    PERMISSIONS.EDIT_TOURNAMENT,
    PERMISSIONS.VIEW_TABLES,
    PERMISSIONS.EDIT_TABLE_RESULT,
    PERMISSIONS.RECORD_NO_SHOW,
    PERMISSIONS.VIEW_PLAYERS,
    PERMISSIONS.UPLOAD_PLAYERS,
    PERMISSIONS.VIEW_TIMER,
    PERMISSIONS.CONTROL_TIMER,
    PERMISSIONS.VIEW_DECK_CHECKS,
    PERMISSIONS.CREATE_DECK_CHECK,
    PERMISSIONS.INIT_TOURNAMENT_ENGINE,
    PERMISSIONS.START_TOURNAMENT,
    PERMISSIONS.END_TOURNAMENT,
    PERMISSIONS.GENERATE_PAIRINGS,
    PERMISSIONS.COMPLETE_ROUND,
    PERMISSIONS.START_TOP_CUT,
    PERMISSIONS.ENTER_RESULT,
    PERMISSIONS.EDIT_RESULT,
    PERMISSIONS.ADD_TOURNAMENT_PLAYER,
    PERMISSIONS.DROP_TOURNAMENT_PLAYER,
    PERMISSIONS.UNDROP_TOURNAMENT_PLAYER,
    PERMISSIONS.EDIT_TOURNAMENT_PLAYER,
    PERMISSIONS.REPAIR_PAIRINGS,
    PERMISSIONS.EDIT_TOURNAMENT_SETTINGS,
    PERMISSIONS.VIEW_STANDINGS,
    PERMISSIONS.VIEW_PAIRINGS,
    PERMISSIONS.VIEW_BRACKET,
  ],

  [ROLES.TEAM_LEAD]: [
    PERMISSIONS.VIEW_TABLES,
    PERMISSIONS.VIEW_PLAYERS,
    PERMISSIONS.ISSUE_PENALTY,
    PERMISSIONS.VIEW_STAFF,
    PERMISSIONS.VIEW_TIMER,
    PERMISSIONS.VIEW_DECK_CHECKS,
    PERMISSIONS.CREATE_DECK_CHECK,
    PERMISSIONS.ENTER_RESULT,
    PERMISSIONS.VIEW_STANDINGS,
    PERMISSIONS.VIEW_PAIRINGS,
    PERMISSIONS.VIEW_BRACKET,
  ],

  [ROLES.FLOOR_JUDGE]: [
    PERMISSIONS.VIEW_TABLES,
    PERMISSIONS.VIEW_PLAYERS,
    PERMISSIONS.ISSUE_PENALTY,
    PERMISSIONS.VIEW_TIMER,
    PERMISSIONS.VIEW_STANDINGS,
    PERMISSIONS.VIEW_PAIRINGS,
    PERMISSIONS.VIEW_BRACKET,
  ],

  [ROLES.SHIELD]: [
    PERMISSIONS.VIEW_TABLES,
    PERMISSIONS.VIEW_PLAYERS,
    PERMISSIONS.VIEW_TIMER,
    PERMISSIONS.VIEW_STANDINGS,
    PERMISSIONS.VIEW_PAIRINGS,
    PERMISSIONS.VIEW_BRACKET,
  ],

  [ROLES.FEATURE_MATCH_JUDGE]: [
    PERMISSIONS.VIEW_TABLES,
    PERMISSIONS.VIEW_PLAYERS,
    PERMISSIONS.VIEW_TIMER,
    PERMISSIONS.VIEW_STANDINGS,
    PERMISSIONS.VIEW_PAIRINGS,
    PERMISSIONS.VIEW_BRACKET,
  ],

  [ROLES.STREAM]: [
    PERMISSIONS.VIEW_PLAYERS,
    PERMISSIONS.VIEW_STANDINGS,
  ],

  [ROLES.PENDING]: [],
};

// ============================================
// ACCOUNT TYPE CHECK FUNCTIONS (Global permissions)
// ============================================

/**
 * Check if user is an admin (uses accountType with legacy fallback)
 */
export function isAdmin(user: UserWithRole | null | undefined): boolean {
  if (!user) return false;
  // New system: check accountType
  if (user.accountType === 'admin') return true;
  // Legacy fallback: check role
  return user.role === ROLES.ADMIN;
}

/**
 * Check if user is a store owner
 */
export function isStoreOwner(user: UserWithRole | null | undefined): boolean {
  if (!user) return false;
  return user.accountType === 'store';
}

/**
 * Check if user is staff (can be assigned to tournaments)
 */
export function isStaff(user: UserWithRole | null | undefined): boolean {
  if (!user) return false;
  // New system: check accountType
  if (user.accountType === 'staff' || user.accountType === 'admin') return true;
  // Legacy fallback: check if approved with a staff role
  return !!(user.role && user.role !== ROLES.PENDING);
}

/**
 * Check if user has staff-level access or higher
 */
export function hasStaffAccess(user: UserWithRole | null | undefined): boolean {
  if (!user) return false;
  return isAdmin(user) || isStoreOwner(user) || isStaff(user);
}

// ============================================
// ROLE CHECK FUNCTIONS (Tournament-specific)
// ============================================

/**
 * Check if a user has a specific legacy role (for backwards compatibility)
 * For tournament-specific role checks, use hasTournamentRole instead
 */
export function hasRole(user: UserWithRole | null | undefined, roles: RoleValue | RoleValue[]): boolean {
  if (!user) return false;

  // Check legacy role field
  if (user.role) {
    const roleArray = Array.isArray(roles) ? roles : [roles];
    if (roleArray.includes(user.role)) return true;
  }

  return false;
}

/**
 * Check if user has a specific tournament role
 * This checks the tournamentRole field which should be set from tournament.staff[uid].role
 */
export function hasTournamentRole(user: UserWithRole | null | undefined, roles: TournamentRole | TournamentRole[]): boolean {
  if (!user) return false;

  // Admin always has access
  if (isAdmin(user)) return true;

  // Check tournament-specific role
  if (user.tournamentRole) {
    const roleArray = Array.isArray(roles) ? roles : [roles];
    if (roleArray.includes(user.tournamentRole)) return true;
  }

  // Legacy fallback: check global role (during transition period)
  if (user.role) {
    const roleArray = Array.isArray(roles) ? roles : [roles];
    // Map legacy RoleValue to TournamentRole for comparison
    const legacyRoleMap: Record<string, TournamentRole> = {
      [ROLES.MANAGER]: 'Manager',
      [ROLES.HEAD_JUDGE]: 'Head Judge',
      [ROLES.FLOOR_JUDGE]: 'Floor Judge',
      [ROLES.TEAM_LEAD]: 'Team Lead',
      [ROLES.SCOREKEEPER]: 'Scorekeeper',
      [ROLES.SHIELD]: 'Shield',
      [ROLES.FEATURE_MATCH_JUDGE]: 'Feature Match',
    };
    const mappedRole = legacyRoleMap[user.role];
    if (mappedRole && roleArray.includes(mappedRole)) return true;
  }

  return false;
}

/**
 * Check if user is a manager (admin or has Manager tournament role)
 */
export function isManager(user: UserWithRole | null | undefined): boolean {
  if (isAdmin(user)) return true;
  // Store owners have Manager-level access to tournaments
  if (isStoreOwner(user)) return true;
  return hasTournamentRole(user, 'Manager');
}

/**
 * Check if user is a head judge or higher (including via HJ team assignment)
 */
export function isHeadJudge(user: UserWithRole | null | undefined): boolean {
  if (isAdmin(user)) return true;
  if (hasTournamentRole(user, ['Manager', 'Head Judge'])) return true;
  return !!(user?.headJudgeTeamAssignments && Object.keys(user.headJudgeTeamAssignments).length > 0);
}

/**
 * Check if user has Feature Match access (including via FM team assignment)
 */
export function isFeatureMatch(user: UserWithRole | null | undefined): boolean {
  if (isAdmin(user)) return true;
  if (hasTournamentRole(user, ['Manager', 'Head Judge', 'Feature Match'])) return true;
  return !!(user?.featureMatchTeamAssignments && Object.keys(user.featureMatchTeamAssignments).length > 0);
}

/**
 * Check if user is a team lead
 */
export function isTeamLead(user: UserWithRole | null | undefined): boolean {
  if (isAdmin(user)) return true;
  return hasTournamentRole(user, ['Manager', 'Head Judge', 'Team Lead']);
}

/**
 * Check if user is acting as a team lead (temporary elevation)
 */
export function isActingTeamLead(user: UserWithRole | null | undefined): boolean {
  if (!user) return false;
  return !!user.actingTeamLeadFor;
}

/**
 * Check if user has a role with at least the specified level
 * Uses legacy role hierarchy - consider using hasTournamentRole for new code
 */
export function hasMinimumRole(user: UserWithRole | null | undefined, minimumRole: RoleValue): boolean {
  // Admin always passes
  if (isAdmin(user)) return true;

  if (!user || !user.role) return false;

  const userLevel = ROLE_HIERARCHY[user.role] || 0;
  const requiredLevel = ROLE_HIERARCHY[minimumRole] || 0;

  return userLevel >= requiredLevel;
}

/**
 * Get the display name for a role
 */
export function getRoleDisplayName(role: RoleValue): string {
  return roleDisplayNames[role] || role;
}

/**
 * Get the color for a role
 */
export function getRoleColor(role: RoleValue | string): string {
  return roleColors[role as RoleValue] || '#8892A0';
}

// ============================================
// TEAM ACCESS FUNCTIONS
// ============================================

/**
 * Check if user has temporary Head Judge access via team assignment
 */
export function hasHeadJudgeTeamAccess(user: UserWithRole | null | undefined): boolean {
  return !!user?.headJudgeTeamAssignments && Object.keys(user.headJudgeTeamAssignments).length > 0;
}

/**
 * Check if user has temporary Feature Match access via team assignment
 */
export function hasFeatureMatchTeamAccess(user: UserWithRole | null | undefined): boolean {
  return !!user?.featureMatchTeamAssignments && Object.keys(user.featureMatchTeamAssignments).length > 0;
}

/**
 * Check if user has temporary Deck Check access via team assignment
 */
export function hasDeckCheckTeamAccess(user: UserWithRole | null | undefined): boolean {
  return !!user?.deckCheckTeamAssignments && Object.keys(user.deckCheckTeamAssignments).length > 0;
}

// ============================================
// PERMISSION FUNCTIONS
// ============================================

/**
 * Map TournamentRole to RoleValue for permission lookup
 */
function tournamentRoleToLegacyRole(tournamentRole: TournamentRole): RoleValue | null {
  const mapping: Record<TournamentRole, RoleValue> = {
    'Manager': ROLES.MANAGER,
    'Head Judge': ROLES.HEAD_JUDGE,
    'Floor Judge': ROLES.FLOOR_JUDGE,
    'Team Lead': ROLES.TEAM_LEAD,
    'Scorekeeper': ROLES.SCOREKEEPER,
    'Shield': ROLES.SHIELD,
    'Feature Match': ROLES.FEATURE_MATCH_JUDGE,
    'Deck Check': ROLES.FLOOR_JUDGE, // Deck Check gets Floor Judge permissions + deck check specific
    'Stream': ROLES.STREAM,
  };
  return mapping[tournamentRole] || null;
}

/**
 * Check if the user is a store-manager proxy (delegated store management).
 */
export function isPermanentManager(user: UserWithRole | null | undefined): boolean {
  return Boolean(user?.permanentStoreId && user?.permanentStoreRole === 'Manager');
}

/**
 * Collect the user's per-tournament assigned roles, mapped to legacy
 * RoleValues for permission lookup. Handles both the newer object shape
 * ({ [tid]: { role } }) and the legacy boolean shape ({ [tid]: true }).
 *
 * Returns both the mapped legacy roles and whether any assignment is a
 * Deck Check role (which carries extra deck-check permissions).
 */
function getAssignmentRoles(user: UserWithRole | null | undefined): {
  roles: RoleValue[];
  hasDeckCheckRole: boolean;
} {
  const roles: RoleValue[] = [];
  let hasDeckCheckRole = false;

  const assignments = user?.tournamentAssignments;
  if (assignments) {
    for (const value of Object.values(assignments)) {
      const role =
        value && typeof value === 'object' ? (value as { role?: string }).role : undefined;
      if (!role) continue;
      if (role === 'Deck Check') hasDeckCheckRole = true;
      const legacy = tournamentRoleToLegacyRole(role as TournamentRole) ?? (role as RoleValue);
      if (rolePermissions[legacy as RoleValue]) roles.push(legacy as RoleValue);
    }
  }

  return { roles, hasDeckCheckRole };
}

/**
 * Manager-level / elevated roles that are EXEMPT from the special-floor
 * override (admin is handled separately as all-access). These users keep
 * their full access even when added to a special floor team.
 */
function isManagerLevel(user: UserWithRole | null | undefined): boolean {
  if (isStoreOwner(user) || isPermanentManager(user)) return true;
  if (user?.role === ROLES.MANAGER) return true;
  if (user?.tournamentRole === 'Manager') return true;
  return getAssignmentRoles(user).roles.includes(ROLES.MANAGER);
}

const DECK_CHECK_PERMISSIONS: Permission[] = [
  PERMISSIONS.VIEW_DECK_CHECKS,
  PERMISSIONS.CREATE_DECK_CHECK,
  PERMISSIONS.COMPLETE_DECK_CHECK,
];

/**
 * The role names a user effectively acts as, AFTER applying the special-floor
 * override. Returns raw role strings (e.g. 'Head Judge', 'Deck Check',
 * 'Manager') matching the names used in route role gates; admins report
 * 'admin'.
 *
 * Used for route-level role gating (ProtectedRoute). Branches on the same
 * predicates (onHJTeam / onDCTeam / isManagerLevel) as getCombinedPermissions
 * so route guards and page guards stay in lockstep:
 *  - Head Judge / Deck Check team membership OVERRIDES the assignment role
 *    for judge-level users.
 *  - Admin / Store / Manager / proxy are exempt and keep their roles.
 *  - Feature Match team is additive.
 */
export function getEffectiveRoleNames(user: UserWithRole | null | undefined): string[] {
  if (!user) return [];
  if (isAdmin(user)) return ['admin'];

  const onHJTeam = hasHeadJudgeTeamAccess(user);
  const onDCTeam = hasDeckCheckTeamAccess(user);
  const elevated = isManagerLevel(user);
  const names = new Set<string>();

  if ((onHJTeam || onDCTeam) && !elevated) {
    // Override: judge-level user reduced to the special team role(s).
    if (onHJTeam) names.add('Head Judge');
    if (onDCTeam) names.add('Deck Check');
  } else {
    // Base: store/proxy Manager + assignment roles + legacy role, plus any
    // special-team roles (additive for these elevated/base users).
    if (isStoreOwner(user) || isPermanentManager(user)) names.add('Manager');
    const assignments = user.tournamentAssignments;
    if (assignments) {
      for (const value of Object.values(assignments)) {
        const role =
          value && typeof value === 'object' ? (value as { role?: string }).role : undefined;
        if (role) names.add(role);
      }
    }
    if (user.tournamentRole) names.add(user.tournamentRole);
    if (user.role) names.add(user.role);
    if (onHJTeam) names.add('Head Judge');
    if (onDCTeam) names.add('Deck Check');
  }

  // Additive memberships.
  if (hasFeatureMatchTeamAccess(user)) names.add('Feature Match');
  if (isActingTeamLead(user) && !isTeamLead(user)) names.add('Team Lead');

  return Array.from(names);
}

/**
 * Get all permissions for a user including team-based permissions.
 *
 * Permission model:
 *  - Admin: everything.
 *  - Base permissions come from the user's tournament-assignment role(s)
 *    (staffAssignments / tournamentAssignments), the legacy global role, and
 *    a Manager baseline for store owners / store proxies.
 *  - SPECIAL-FLOOR OVERRIDE: a judge-level user placed on a Head Judge or
 *    Deck Check floor team is reduced to ONLY that team's permissions — their
 *    assignment role is set aside while they're on that special team. Removing
 *    them from the special team restores their assignment-role permissions.
 *  - Elevated users (admin / store / Manager / store proxy) are EXEMPT from
 *    the override: they keep full access and gain the team permissions on top.
 *  - Feature Match team membership is always ADDITIVE (never an override).
 */
function getCombinedPermissions(user: UserWithRole | null | undefined): Permission[] {
  if (!user) return [];

  // Admin gets all permissions
  if (isAdmin(user)) {
    return Object.values(PERMISSIONS);
  }

  const onHJTeam = hasHeadJudgeTeamAccess(user);
  const onDCTeam = hasDeckCheckTeamAccess(user);
  const elevated = isManagerLevel(user);

  let permissions: Permission[] = [];

  if ((onHJTeam || onDCTeam) && !elevated) {
    // OVERRIDE: special floor team replaces the assignment role for
    // judge-level users. They get only the special team's permissions.
    if (onHJTeam) {
      permissions = Array.from(new Set([...permissions, ...(rolePermissions[ROLES.HEAD_JUDGE] || [])]));
    }
    if (onDCTeam) {
      permissions = Array.from(new Set([...permissions, ...DECK_CHECK_PERMISSIONS]));
    }
  } else {
    // BASE: store/proxy Manager baseline + assignment roles + legacy role.
    if (isStoreOwner(user) || isPermanentManager(user)) {
      permissions = [...(rolePermissions[ROLES.MANAGER] || [])];
    }

    // Tournament-assignment roles (the per-event role the user was given).
    const { roles: assignmentRoles, hasDeckCheckRole } = getAssignmentRoles(user);
    for (const role of assignmentRoles) {
      permissions = Array.from(new Set([...permissions, ...(rolePermissions[role] || [])]));
    }
    if (hasDeckCheckRole) {
      permissions = Array.from(new Set([...permissions, ...DECK_CHECK_PERMISSIONS]));
    }

    // Tournament-specific role (new system) — kept for back-compat with any
    // caller that still sets it.
    if (user.tournamentRole) {
      const legacyRole = tournamentRoleToLegacyRole(user.tournamentRole);
      if (legacyRole) {
        permissions = Array.from(new Set([...permissions, ...(rolePermissions[legacyRole] || [])]));
      }
      if (user.tournamentRole === 'Deck Check') {
        permissions = Array.from(new Set([...permissions, ...DECK_CHECK_PERMISSIONS]));
      }
    }

    // Legacy global role fallback.
    if (user.role) {
      permissions = Array.from(new Set([...permissions, ...(rolePermissions[user.role] || [])]));
    }

    // Elevated users on a special team keep their base AND gain the team's
    // permissions (additive, not stripped).
    if (onHJTeam) {
      permissions = Array.from(new Set([...permissions, ...(rolePermissions[ROLES.HEAD_JUDGE] || [])]));
    }
    if (onDCTeam) {
      permissions = Array.from(new Set([...permissions, ...DECK_CHECK_PERMISSIONS]));
    }
  }

  // Add Team Lead permissions if user is Acting Lead
  if (isActingTeamLead(user) && !isTeamLead(user)) {
    const tlPermissions = rolePermissions[ROLES.TEAM_LEAD] || [];
    permissions = Array.from(new Set([...permissions, ...tlPermissions]));
  }

  // Feature Match team membership is ADDITIVE — adds FM + deck-check
  // permissions on top of whatever the user already has.
  if (hasFeatureMatchTeamAccess(user)) {
    const fmPermissions = rolePermissions[ROLES.FEATURE_MATCH_JUDGE] || [];
    permissions = Array.from(new Set([...permissions, ...fmPermissions, ...DECK_CHECK_PERMISSIONS]));
  }

  return permissions;
}

/**
 * Check if user has a specific permission
 */
export function hasPermission(user: UserWithRole | null | undefined, permission: Permission): boolean {
  if (!user) return false;

  const permissions = getCombinedPermissions(user);
  return permissions.includes(permission);
}

/**
 * Check if user has all specified permissions
 */
export function hasAllPermissions(user: UserWithRole | null | undefined, permissions: Permission[]): boolean {
  return permissions.every(p => hasPermission(user, p));
}

/**
 * Check if user has any of the specified permissions
 */
export function hasAnyPermission(user: UserWithRole | null | undefined, permissions: Permission[]): boolean {
  return permissions.some(p => hasPermission(user, p));
}

/**
 * Get all permissions for a user (including team-based permissions)
 */
export function getUserPermissions(user: UserWithRole | null | undefined): Permission[] {
  return getCombinedPermissions(user);
}

// ============================================
// TOURNAMENT ENGINE PERMISSION HELPERS
// ============================================

/**
 * Check if user can manage tournament engine (start, generate pairings, etc.)
 */
export function canManageTournament(user: UserWithRole | null | undefined): boolean {
  return hasAnyPermission(user, [
    PERMISSIONS.START_TOURNAMENT,
    PERMISSIONS.GENERATE_PAIRINGS,
    PERMISSIONS.COMPLETE_ROUND,
  ]);
}

/**
 * Check if user can enter results
 */
export function canEnterResults(user: UserWithRole | null | undefined): boolean {
  return hasPermission(user, PERMISSIONS.ENTER_RESULT);
}

/**
 * Check if user can edit past results
 */
export function canEditResults(user: UserWithRole | null | undefined): boolean {
  return hasPermission(user, PERMISSIONS.EDIT_RESULT);
}

/**
 * Check if user can manage players (add/drop/undrop)
 */
export function canManageTournamentPlayers(user: UserWithRole | null | undefined): boolean {
  return hasAnyPermission(user, [
    PERMISSIONS.ADD_TOURNAMENT_PLAYER,
    PERMISSIONS.DROP_TOURNAMENT_PLAYER,
    PERMISSIONS.UNDROP_TOURNAMENT_PLAYER,
  ]);
}

/**
 * Check if user can perform repairs
 */
export function canRepairPairings(user: UserWithRole | null | undefined): boolean {
  return hasPermission(user, PERMISSIONS.REPAIR_PAIRINGS);
}

/**
 * Check if user can start top cut
 */
export function canStartTopCut(user: UserWithRole | null | undefined): boolean {
  return hasPermission(user, PERMISSIONS.START_TOP_CUT);
}

/**
 * Check if user can edit tournament settings
 */
export function canEditTournamentSettings(user: UserWithRole | null | undefined): boolean {
  return hasPermission(user, PERMISSIONS.EDIT_TOURNAMENT_SETTINGS);
}

// ============================================
// DEFAULT EXPORT
// ============================================

const permissionsService = {
  // Account type checks (global)
  isAdmin,
  isStoreOwner,
  isStaff,
  hasStaffAccess,
  // Tournament role checks
  hasRole,
  hasTournamentRole,
  isManager,
  isHeadJudge,
  isFeatureMatch,
  isTeamLead,
  isActingTeamLead,
  hasMinimumRole,
  // Display helpers
  getRoleDisplayName,
  getRoleColor,
  // Permission checks
  hasPermission,
  hasAllPermissions,
  hasAnyPermission,
  getUserPermissions,
  // Team access
  hasHeadJudgeTeamAccess,
  hasFeatureMatchTeamAccess,
  hasDeckCheckTeamAccess,
  // Constants
  PERMISSIONS,
  // Tournament engine helpers
  canManageTournament,
  canEnterResults,
  canEditResults,
  canManageTournamentPlayers,
  canRepairPairings,
  canStartTopCut,
  canEditTournamentSettings,
};

export default permissionsService;
